import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FiPlus, FiTag } from "react-icons/fi";
import axiosInstance from "../config/axiosInstance";
import NoData from "../components/NoData";
import ResponsiveModal from "../components/ResponsiveModal";
import BreadcrumbTrail from "../components/BreadcrumbTrail";
import "./CategoriesPage.css";

const SubcategoriesPage = () => {
  const { id } = useParams();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [togglingId, setTogglingId] = useState(null);

  const fetchCategory = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/categories/${id}`);
      setCategory(response.data?.data || null);
    } catch {
      setCategory(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategory();
    // eslint-disable-next-line
  }, [id]);

  const handleToggle = async (sub, currentlyActive) => {
    setTogglingId(sub.id);
    try {
      await axiosInstance.put(`/categories/${sub.id}/status`, {
        isActive: !currentlyActive,
      });
      setCategory((prev) => ({
        ...prev,
        subcategories: prev.subcategories.map((s) =>
          s.id === sub.id ? { ...s, isActive: !currentlyActive } : s
        ),
      }));
    } catch {
      // silently ignore
    } finally {
      setTogglingId(null);
    }
  };

  const handleCreateSubcategory = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      setError("Informe o nome da subcategoria.");
      return;
    }
    setCreating(true);
    setError("");
    try {
      await axiosInstance.post("/categories", { name, parentId: id });
      setName("");
      setIsCreateModalOpen(false);
      fetchCategory();
    } catch {
      setError("Erro ao criar subcategoria.");
    } finally {
      setCreating(false);
    }
  };

  if (loading)
    return (
      <div className="muted" style={{ padding: 32, textAlign: "center" }}>
        Carregando...
      </div>
    );
  if (!category) return <NoData title="Categoria não encontrada" />;

  const subs = category.subcategories || [];

  return (
    <div className="fade-in page-stack">
      <BreadcrumbTrail
        items={[
          { label: "Categorias", to: "/admin/categorias" },
          {
            label: category.nome || category.name,
            to: `/admin/categorias/${category.id}/subcategorias`,
          },
        ]}
      />

      <div className="page-header" style={{ marginTop: -8 }}>
        <div>
          <h2>{category.nome || category.name}</h2>
          <p>
            {subs.length > 0
              ? `${subs.length} subcategor${subs.length === 1 ? "ia" : "ias"} cadastrada${subs.length === 1 ? "" : "s"}`
              : "Nenhuma subcategoria cadastrada ainda."}
          </p>
        </div>
        <button
          className="button button--primary"
          type="button"
          onClick={() => setIsCreateModalOpen(true)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <FiPlus size={15} />
          Nova subcategoria
        </button>
      </div>

      <ResponsiveModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Nova subcategoria"
        size="sm"
      >
        <form onSubmit={handleCreateSubcategory} className="space-y-3">
          <div className="field" style={{ margin: 0 }}>
            <label className="label">Nome da subcategoria</label>
            <input
              className="input"
              type="text"
              placeholder="Ex.: Feijão, Arroz, Milho"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              required
            />
          </div>
          {error ? <div className="alert">{error}</div> : null}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <button
              className="button button--ghost"
              type="button"
              onClick={() => setIsCreateModalOpen(false)}
              disabled={creating}
            >
              Cancelar
            </button>
            <button className="button button--primary" type="submit" disabled={creating}>
              {creating ? "Cadastrando..." : "Cadastrar"}
            </button>
          </div>
        </form>
      </ResponsiveModal>

      <div className="section-card">
        {subs.length === 0 ? (
          <NoData
            title="Nenhuma subcategoria"
            description="Clique em 'Nova subcategoria' para adicionar a primeira."
          />
        ) : (
          <div className="cat-list">
            {subs.map((sub, index) => {
              const isActive =
                typeof sub?.isActive === "boolean"
                  ? sub.isActive
                  : String(sub?.status || "").toLowerCase() === "ativo";
              const isToggling = togglingId === sub.id;

              return (
                <div key={sub.id ?? index} className="cat-row" style={{ cursor: "default" }}>
                  <FiTag size={15} className={`cat-row__icon${!isActive ? " cat-row__icon--inactive" : ""}`} />
                  <span className="cat-row__name" style={{ flex: 1 }}>
                    {sub.nome || sub.name || "-"}
                  </span>
                  <button
                    className={`cat-toggle ${isActive ? "cat-toggle--on" : "cat-toggle--off"}`}
                    type="button"
                    onClick={() => handleToggle(sub, isActive)}
                    disabled={isToggling}
                    title={isActive ? "Clique para desativar" : "Clique para ativar"}
                  >
                    {isToggling ? "..." : isActive ? "Ativa" : "Inativa"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubcategoriesPage;
